import React, { FC } from 'react';

import cx from 'classnames';

import { Img, Link } from 'components/base';
import { ModuleHeader } from 'components/modules';

import sanityImgUtil from 'utils/sanityImgUtil';
import sanityImgSrcSetUtil from 'utils/sanityImgSrcSetUtil';
import formatFullName from 'utils/formatFullName';

import {
  GeneralModuleColorScheme,
  GeneralModuleColor,
} from 'constants/ColorScheme';
import Language from 'constants/Language';
import { RouteMap } from 'constants/Routes';

import { TeamListModule as ITeamListModule } from 'types';

interface Props {
  module: ITeamListModule;
}

const TeamListModule: FC<Props> = ({ module }) => {
  const { moduleHeader, contents, moduleOptions } = module;
  const headerTitle = moduleHeader.title;
  const headerDescription = moduleHeader.description;
  const colorScheme =
    GeneralModuleColorScheme[module.moduleOptions.color as GeneralModuleColor];

  return (
    <div
      className={cx(`TeamListModule color-black flex flex-col`, {
        'border-top-gray-lighter': moduleOptions.showBorderTop,
        'border-bottom-gray-lighter': moduleOptions.showBorderBottom,
      })}
    >
      <div className="TeamListModule__header module-content-padding-x">
        <ModuleHeader title={headerTitle} description={headerDescription} />
      </div>
      <div className="TeamListModule__cards flex flex-row flex-wrap">
        {contents.map((teamMember, index) => {
          const bgColorClass =
            index % 2 === 0
              ? `bg-color-${colorScheme.background}`
              : `bg-color-${colorScheme.darkBackground}`;

          if (teamMember.type === 'author') {
            const fullName = formatFullName(teamMember);

            return (
              <div
                className={`TeamListModule__card ${bgColorClass} col-6 md:col-4 text-sm md-up_text-md`}
                key={`${teamMember.id}`}
              >
                <Link
                  ariaLabel={Language.t('Global.teamMemberButtonAriaLabel', {
                    name: fullName,
                  })}
                  to={`${RouteMap.AUTHOR.base}${teamMember.slug}`}
                  containerClassName="TeamListModule__card-image"
                >
                  <Img
                    alt={teamMember.image.alt}
                    className="w100"
                    sizes="(maxWidth: 768px) 400px, (maxWidth: 1024px) 500px, 650px"
                    src={sanityImgUtil(teamMember.image, 400)}
                    srcSet={sanityImgSrcSetUtil(
                      teamMember.image,
                      400,
                      500,
                      650
                    )}
                    preloadContainerClassName="w100"
                    dimensions={teamMember.image.metadata?.dimensions}
                    crop={teamMember.image.crop}
                  />
                </Link>
                <div className="TeamListModule__card-info m2_25">
                  {!!teamMember.firstName && !!teamMember.lastName && (
                    <Link
                      ariaLabel={Language.t(
                        'Global.teamMemberButtonAriaLabel',
                        {
                          name: fullName,
                        }
                      )}
                      to={`${RouteMap.AUTHOR.base}${teamMember.slug}`}
                      containerClassName="TeamListModule__title-button inline-flex"
                    >
                      <span
                        className={`TeamListModule__title color-${colorScheme.text} mb_5 link-border-bottom-black-hidden`}
                      >
                        {fullName}
                      </span>
                    </Link>
                  )}
                  {!!teamMember.jobTitle && (
                    <span className="color-gray block">
                      {teamMember.jobTitle}
                    </span>
                  )}
                </div>
              </div>
            );
          }

          return (
            <div
              className={`TeamListModule__card ${bgColorClass} col-6 md:col-4 text-sm md-up_text-md`}
              key={`${teamMember.id}`}
            >
              <div className="TeamListModule__card-image">
                <Img
                  alt={teamMember.image.alt}
                  className="w100"
                  sizes="(maxWidth: 768px) 400px, (maxWidth: 1024px) 500px, 650px"
                  src={sanityImgUtil(teamMember.image, 400)}
                  srcSet={sanityImgSrcSetUtil(teamMember.image, 400, 500, 650)}
                  preloadContainerClassName="w100"
                  dimensions={teamMember.image.metadata?.dimensions}
                  crop={teamMember.image.crop}
                />
              </div>
              <div className="TeamListModule__card-info m2_25">
                {!!teamMember.firstName && !!teamMember.lastName && (
                  <span className={`color-${colorScheme.text} mb_5`}>
                    {formatFullName(teamMember)}
                  </span>
                )}
                {!!teamMember.jobTitle && (
                  <span className="color-gray block">
                    {teamMember.jobTitle}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TeamListModule;
